import { useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMenuVariant } from '@/hooks/useMenuVariant'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { FeatureFlag } from '@/constants/featureFlags'
import tkLogo from '@/assets/tk-logga.png'
import {
  UserCheck,
  Settings,
  Upload,
  Users,
  Car,
  ChevronDown,
  Menu
} from 'lucide-react'
import { sv, externalUrls } from '@/lib/constants'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'

interface NavLink {
  href: string
  label: string
  icon: typeof UserCheck
  active: boolean
}

export function Navbar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { displayName, userEmail, signOut, isAdmin } = useAuth()
  const { variant: menuVariant, toggle: toggleMenuVariant } = useMenuVariant()
  const hasNewMenuFlag = useFeatureFlag(FeatureFlag.NEW_MENU)
  const [isMobile, setIsMobile] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const headerRef = useRef<HTMLElement>(null)

  // Check viewport width for mobile/desktop
  useEffect(() => {
    const checkWidth = () => {
      if (headerRef.current) {
        const width = headerRef.current.offsetWidth
        setIsMobile(width < 768)
      }
    }

    checkWidth()
    const resizeObserver = new ResizeObserver(checkWidth)
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const handleSignOut = async () => {
    await signOut()
    window.location.href = externalUrls.landingPage
  }

  const isActive = (href: string) => {
    if (href === '/salaryreport') {
      return location.pathname === href || location.pathname.startsWith('/salaryreport/')
    }
    return location.pathname === href
  }

  // Current flat navigation (default)
  const currentNavLinks: NavLink[] = [
    { href: '/salaryreport', label: 'Lönerapport', icon: UserCheck, active: isActive('/salaryreport') },
    { href: '/upload', label: 'Uppladdning', icon: Upload, active: isActive('/upload') },
    { href: '/forare', label: 'Förare', icon: Users, active: isActive('/forare') },
    { href: '/vehicles', label: 'Fordon', icon: Car, active: isActive('/vehicles') },
    { href: '/configuration', label: sv.navigation.configuration, icon: Settings, active: isActive('/configuration') },
  ]

  // V1: Perioder as primary, rest as secondary
  const v1PrimaryLink: NavLink = { href: '/periods', label: 'Perioder', icon: UserCheck, active: isActive('/periods') }
  const v1SecondaryLinks: NavLink[] = [
    { href: '/salaryreport', label: 'Historik', icon: UserCheck, active: isActive('/salaryreport') },
    { href: '/forare', label: 'Förare', icon: Users, active: isActive('/forare') },
    { href: '/vehicles', label: 'Fordon', icon: Car, active: isActive('/vehicles') },
    { href: '/configuration', label: 'Inställningar', icon: Settings, active: isActive('/configuration') },
  ]

  const userInitials = displayName
    ? displayName.split(' ').map(n => n[0]).join('').toUpperCase()
    : 'U'

  const userMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 gap-2 px-2">
          <Avatar className="h-6 w-6">
            <AvatarFallback className="text-xs">
              {userInitials}
            </AvatarFallback>
          </Avatar>
          <ChevronDown className="h-3 w-3" />
          <span className="sr-only">Användarmeny</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{displayName}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {userEmail}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/profile')}>
          Profil
        </DropdownMenuItem>
        {isAdmin && (
          <DropdownMenuItem onClick={() => navigate('/admin')}>
            Administration
          </DropdownMenuItem>
        )}
        {hasNewMenuFlag && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={toggleMenuVariant}>
              {menuVariant === 'v1' ? 'Använd det gamla flödet' : 'Prova nya arbetsflödet'}
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut}>
          Logga ut
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 md:px-6"
    >
      <div className="container mx-auto flex h-16 max-w-screen-2xl items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          {/* Mobile hamburger menu */}
          {isMobile && (
            <Popover open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                >
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Öppna meny</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-48 p-2">
                <div className="flex flex-col gap-1">
                  {/* Mobile: V1 shows Perioder first with emphasis */}
                  {menuVariant === 'v1' && (
                    <>
                      <button
                        onClick={() => {
                          navigate(v1PrimaryLink.href)
                          setMobileMenuOpen(false)
                        }}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-bold transition-colors bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer',
                        )}
                      >
                        <v1PrimaryLink.icon className="h-4 w-4" />
                        {v1PrimaryLink.label}
                      </button>
                      <div className="border-t my-1" />
                    </>
                  )}
                  {(menuVariant === 'v1' ? v1SecondaryLinks : currentNavLinks).map((link) => {
                    const Icon = link.icon
                    return (
                      <button
                        key={link.href}
                        onClick={() => {
                          navigate(link.href)
                          setMobileMenuOpen(false)
                        }}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground cursor-pointer',
                          link.active
                            ? 'bg-accent text-accent-foreground'
                            : 'text-foreground/80'
                        )}
                      >
                        <Icon className="h-4 w-4" />
                        {link.label}
                      </button>
                    )
                  })}
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* Logo and app name */}
          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-primary hover:text-primary/90 transition-colors cursor-pointer"
            >
              <img src={tkLogo} alt="Logo" className="h-8 w-8" />
              <span className="hidden font-bold text-xl sm:inline-block">
                taxiadministration.se
              </span>
            </button>

            {/* Desktop navigation - variant based */}
            {!isMobile && menuVariant === 'current' && (
              <nav className="flex items-center gap-1">
                {currentNavLinks.map((link) => {
                  const Icon = link.icon
                  return (
                    <button
                      key={link.href}
                      onClick={() => navigate(link.href)}
                      className={cn(
                        'group inline-flex h-9 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 cursor-pointer gap-2',
                        link.active
                          ? 'bg-accent text-accent-foreground'
                          : 'text-foreground/80 hover:text-foreground'
                      )}
                      title={link.label}
                    >
                      <Icon className="h-4 w-4" />
                      {link.label}
                    </button>
                  )
                })}
              </nav>
            )}

            {/* V1: Primary button only (secondary moved to right) */}
            {!isMobile && menuVariant === 'v1' && (
              <Button
                onClick={() => navigate(v1PrimaryLink.href)}
                variant={v1PrimaryLink.active ? 'default' : 'outline'}
                size="sm"
                className="gap-2 font-semibold"
              >
                <v1PrimaryLink.icon className="h-4 w-4" />
                {v1PrimaryLink.label}
              </Button>
            )}

            {/* V2: Add your variant here */}
            {!isMobile && menuVariant === 'v2' && (
              <nav className="flex items-center gap-1">
                <span className="text-muted-foreground text-sm">V2 variant - TODO</span>
              </nav>
            )}
          </div>
        </div>

        {/* Right side - V1 secondary links + User menu */}
        <div className="flex items-center gap-4">
          {!isMobile && menuVariant === 'v1' && (
            <nav className="flex items-center gap-1">
              {v1SecondaryLinks.map((link) => {
                const Icon = link.icon
                return (
                  <button
                    key={link.href}
                    onClick={() => navigate(link.href)}
                    className={cn(
                      'inline-flex h-8 items-center justify-center rounded-md px-3 py-1 text-sm transition-colors hover:bg-accent hover:text-accent-foreground cursor-pointer gap-1.5',
                      link.active
                        ? 'bg-accent/50 text-accent-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                    title={link.label}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {link.label}
                  </button>
                )
              })}
            </nav>
          )}
          {userMenu}
        </div>
      </div>
    </header>
  )
}
